/** Planar face detection (#651) — pure geometry, no Paper.js.
 *
 *  Given a planar arrangement (nodes + undirected edges that only meet at
 *  nodes), enumerates the bounded faces and finds the smallest one containing a
 *  point. Used by the region click-to-fill tool: overlapping construction lines
 *  are noded into this arrangement, the user clicks inside a region, and we
 *  trace the face boundary back to construction segments.
 *
 *  Algorithm: build two half-edges per edge, sort the half-edges leaving each
 *  node by angle, and define next(h) as the half-edge immediately clockwise from
 *  h's reverse at its destination. Walking next() partitions all half-edges into
 *  face cycles; the bounded faces are the ones with positive (CCW) signed area
 *  (the single outer face comes out clockwise). */

export interface Pt {
  x: number;
  y: number;
}

export interface FaceEdge {
  /** Index into the input `edges` array. */
  edge: number;
  /** Directed traversal: from this node index … */
  from: number;
  /** … to this node index. */
  to: number;
}

interface HalfEdge {
  edge: number;
  from: number;
  to: number;
  angle: number;
}

const TAU = Math.PI * 2;

function angleOf(a: Pt, b: Pt): number {
  return Math.atan2(b.y - a.y, b.x - a.x);
}

function signedArea(poly: Pt[]): number {
  let s = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i]!;
    const q = poly[(i + 1) % poly.length]!;
    s += p.x * q.y - q.x * p.y;
  }
  return s / 2;
}

/** Even-odd point-in-polygon. */
export function pointInPolygon(poly: Pt[], p: Pt): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]!;
    const b = poly[j]!;
    const intersects =
      a.y > p.y !== b.y > p.y &&
      p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

/** Every bounded face of the arrangement, each as an ordered loop of directed
 *  half-edges (CCW). The unbounded outer face is excluded. */
export function findFaces(nodes: Pt[], edges: { a: number; b: number }[]): FaceEdge[][] {
  const halves: HalfEdge[] = [];
  for (let e = 0; e < edges.length; e++) {
    const { a, b } = edges[e]!;
    if (a === b) continue; // drop degenerate loops
    halves.push({ edge: e, from: a, to: b, angle: angleOf(nodes[a]!, nodes[b]!) });
    halves.push({ edge: e, from: b, to: a, angle: angleOf(nodes[b]!, nodes[a]!) });
  }

  // Outgoing half-edges per node, sorted by angle ascending.
  const outgoing = new Map<number, number[]>();
  for (let h = 0; h < halves.length; h++) {
    const arr = outgoing.get(halves[h]!.from) ?? [];
    arr.push(h);
    outgoing.set(halves[h]!.from, arr);
  }
  for (const arr of outgoing.values()) {
    arr.sort((x, y) => halves[x]!.angle - halves[y]!.angle);
  }
  const twin = (h: number): number => (h % 2 === 0 ? h + 1 : h - 1);
  const posInRing = new Map<number, number>();
  for (const arr of outgoing.values()) {
    arr.forEach((h, i) => posInRing.set(h, i));
  }

  /** The next half-edge around the face on the left of `h`: at h's destination,
   *  the one immediately clockwise from h's reverse direction. */
  const next = (h: number): number => {
    const rev = twin(h); // departs h.to back toward h.from
    const ring = outgoing.get(halves[h]!.to)!;
    const i = posInRing.get(rev)!;
    return ring[(i - 1 + ring.length) % ring.length]!;
  };

  const visited = new Array<boolean>(halves.length).fill(false);
  const faces: FaceEdge[][] = [];
  for (let start = 0; start < halves.length; start++) {
    if (visited[start]) continue;
    const loop: number[] = [];
    let h = start;
    let guard = 0;
    while (!visited[h] && guard++ < halves.length + 1) {
      visited[h] = true;
      loop.push(h);
      h = next(h);
    }
    if (loop.length < 3) continue;
    const poly = loop.map((hi) => nodes[halves[hi]!.from]!);
    if (signedArea(poly) <= 1e-9) continue; // outer face / degenerate → skip
    faces.push(
      loop.map((hi) => ({
        edge: halves[hi]!.edge,
        from: halves[hi]!.from,
        to: halves[hi]!.to,
      })),
    );
  }
  return faces;
}

/** The smallest bounded face containing `p`, as an ordered CCW loop of directed
 *  half-edges — or null when `p` lies in the unbounded region. */
export function faceContainingPoint(
  nodes: Pt[],
  edges: { a: number; b: number }[],
  p: Pt,
): FaceEdge[] | null {
  const faces = findFaces(nodes, edges);
  let best: FaceEdge[] | null = null;
  let bestArea = Infinity;
  for (const face of faces) {
    const poly = face.map((fe) => nodes[fe.from]!);
    if (!pointInPolygon(poly, p)) continue;
    const area = Math.abs(signedArea(poly));
    if (area < bestArea) {
      bestArea = area;
      best = face;
    }
  }
  return best;
}

/** The smallest bounded face containing `p`, plus whether it borders any other
 *  bounded face (i.e. there's an adjacent region that could be merged into it).
 *  Null when `p` is in the unbounded region. */
export function faceAndNeighbors(
  nodes: Pt[],
  edges: { a: number; b: number }[],
  p: Pt,
): { face: FaceEdge[]; hasNeighbor: boolean } | null {
  const faces = findFaces(nodes, edges);
  let bestIdx = -1;
  let bestArea = Infinity;
  for (let i = 0; i < faces.length; i++) {
    const poly = faces[i]!.map((fe) => nodes[fe.from]!);
    if (!pointInPolygon(poly, p)) continue;
    const area = Math.abs(signedArea(poly));
    if (area < bestArea) {
      bestArea = area;
      bestIdx = i;
    }
  }
  if (bestIdx < 0) return null;

  // Map each directed half-edge to its owning face, then check whether any of
  // the chosen face's edges has its twin in a *different* face.
  const heFace = new Map<string, number>();
  faces.forEach((f, fi) =>
    f.forEach((fe) => heFace.set(`${fe.edge}:${fe.from}:${fe.to}`, fi)),
  );
  const face = faces[bestIdx]!;
  let hasNeighbor = false;
  for (const fe of face) {
    const twinFace = heFace.get(`${fe.edge}:${fe.to}:${fe.from}`);
    if (twinFace !== undefined && twinFace !== bestIdx) {
      hasNeighbor = true;
      break;
    }
  }
  return { face, hasNeighbor };
}

export { signedArea, angleOf, TAU };
